import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageToImageAi = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-to-image-ai',
			name: 'Image to Image AI Generator — Transform Any Photo by Lift',
			description:
				'Turn any picture into a new creation with image to image AI. Upload a photo, describe the change, pick a model and style, and get a reimagined result in seconds. Try Lift today!',
			url: 'https://liftapp.ai/image-to-image-ai',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-to-image-ai#product' },
				{ '@id': 'https://liftapp.ai/image-to-image-ai#howto' },
				{ '@id': 'https://liftapp.ai/image-to-image-ai#benefits' },
				{ '@id': 'https://liftapp.ai/image-to-image-ai#faq' },
				{ '@id': 'https://liftapp.ai/image-to-image-ai#why-lift' },
				{ '@id': 'https://liftapp.ai/image-to-image-ai#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-to-image-ai#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-to-image-ai#product',
			name: 'AI Image to Image Generator',
			description:
				'Turn any picture into a new creation with image to image AI inside Lift.',
			url: 'https://liftapp.ai/image-to-image-ai',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-to-image-ai#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Add your image',
					text: 'Upload the photo or picture you want to transform — it becomes the reference for your new generation',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Describe the result',
					text: 'Write a prompt that tells the AI how to change your image, or tap Improve Prompt to make it more detailed',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Choose a model, style, and ratio',
					text: 'Pick the AI model and visual style that fit your idea — with a reference image added, the aspect ratio can even match your original automatically',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-image-ai#benefits',
			name: 'Who can benefit from AI Image to Image Generator?',
			description:
				"Content creators, marketers, designers, online sellers, and AI art enthusiasts can benefit from Lift's image to image AI generator for transforming existing photos into new creations.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Give existing shots a second life. Turn one photo into fresh visuals for Instagram, TikTok, and YouTube — new style, new setting, no reshoots',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Transform a single product photo into a whole set of campaign-ready variations. Test different looks and scenes without booking another shoot',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers & freelancers — Start from a sketch, draft, or reference picture and explore art directions fast. Remix any result to keep refining it step by step',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Online sellers & small businesses — Place plain catalog photos into lifestyle scenes that sell. Get polished product imagery without studios or design software',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'AI art enthusiasts — Reimagine your photos as illustrations, paintings, or cinematic frames. Every generation lands in your gallery, ready to revisit or push further',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Image to Image AI Examples by Lift',
				description:
					'Before and after results from the Lift image to image AI generator.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-to-image-ai#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is an image to image AI generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'An image to image AI generator (also known as img2img) creates a new picture based on one you already have. Lift uses your uploaded image as a reference and your text prompt as the instruction, so the result keeps the essence of the original while changing its style, scene, or details.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Image to Image Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload the picture you want to transform, describe the change you have in mind, and choose a model and style. Hit Generate and receive your reimagined image in seconds — no design experience needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I keep refining a generated image?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Use Remix to take any result as the base for your next generation and steer it with a new prompt, or Regenerate to rerun the same settings for a fresh take. You can repeat this as many times as it takes to land exactly the look you want.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-image-ai#why-lift',
			name: 'Why Choose the Image to Image AI Generator by Lift',
			description:
				"Lift's Image to Image AI Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why Choose the Image to Image AI Generator by Lift',
				description: 'Lift image to image AI generator results across styles.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-image-ai#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-to-image-ai#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Image to Image Generator',
					item: 'https://liftapp.ai/image-to-image-ai',
				},
			],
		},
	],
};
